import { useState, useRef, useEffect } from 'react'
import { useWebSocket } from '../context/WebSocketContext'
import { useChat } from '../context/ChatContext'
import { useA2FSettings } from '../context/A2FSettingsContext'
import { useUrlParams } from '../hooks/useUrlParams'
import styles from './ChatPage.module.css'
import A2FSettingsSidebar from '../components/chat/A2FSettingsSidebar'
import StoryDebugSidebar from '../components/chat/StoryDebugSidebar'
import HelpModal from '../components/chat/HelpModal'

export default function ChatPage() {
  const { connected, sessionId, sessionFeatures, sessionCharacter, sessionStory, sessionMode, sessionTransmission } = useWebSocket()
  const { messages, metrics, suggestions, mode, setMode, sendMessage, storyDebug, storytellerPending, resetSession, buildVersion } = useChat()
  const { sendA2FConfig } = useA2FSettings()
  const urlParams = useUrlParams()
  const [inputText, setInputText] = useState('')
  const [a2fOpen, setA2fOpen] = useState(false)
  const [storyDebugOpen, setStoryDebugOpen] = useState(() => localStorage.getItem('storyDebugOpen') === '1')
  const [helpOpen, setHelpOpen] = useState(false)
  const [wsCopied, setWsCopied] = useState(false)
  const chatRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight
    }
  }, [messages])

  function handleSend() {
    if (!inputText.trim()) return
    sendMessage(inputText)
    setInputText('')
  }

  function handleKeyPress(e: React.KeyboardEvent) {
    if (e.key === 'Enter') handleSend()
  }

  function toggleStoryDebug() {
    const next = !storyDebugOpen
    setStoryDebugOpen(next)
    localStorage.setItem('storyDebugOpen', next ? '1' : '0')
  }

  function startStorySession() {
    localStorage.removeItem('sessionId')
    const url = new URL(window.location.href)
    url.searchParams.set('features', 'storyteller')
    window.location.href = url.toString()
  }

  function setUrlParam(key: string, value: string) {
    const url = new URL(window.location.href)
    if (value) {
      url.searchParams.set(key, value)
    } else {
      url.searchParams.delete(key)
    }
    window.location.href = url.toString()
  }

  function buildWsUrl() {
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${protocol}//${location.host}/ws?session_id=${sessionId}`
  }

  function copyWsUrl() {
    if (!sessionId) return
    navigator.clipboard.writeText(buildWsUrl()).then(() => {
      setWsCopied(true)
      setTimeout(() => setWsCopied(false), 2000)
    })
  }

  const hasStoryteller = !!sessionFeatures.storyteller

  const fmtMs = (v?: number) => v !== undefined ? `${v}ms` : '--'

  const activeFeatures = Object.entries(sessionFeatures)
    .filter(([, v]) => v)
    .map(([k]) => k)

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>
          Convorcher Chat
          {buildVersion && <span className={styles.version}>{buildVersion}</span>}
        </h1>
        <div className={styles.headerRight}>
          {hasStoryteller && (
            <button
              className={`${styles.btn} ${storyDebugOpen ? styles.btnActive : ''}`}
              onClick={toggleStoryDebug}
              title="Story Debug"
            >Story</button>
          )}
          <button className={styles.btn} onClick={() => setHelpOpen(true)} title="Help">?</button>
          <button
            className={`${styles.btn} ${a2fOpen ? styles.btnAccent : ''}`}
            onClick={() => setA2fOpen(v => !v)}
            title="A2F Settings"
          >⚙</button>
        </div>
      </header>

      <div className={styles.controls}>
        <div className={styles.metrics}>
          <span>LLM: {fmtMs(metrics.llm_first_sentence_ms)}</span>
          <span>TTS Fast: {fmtMs(metrics.tts_fast_ms)}</span>
          <span>TTS Quality: {fmtMs(metrics.tts_quality_ms)}</span>
          <span>A2F: {fmtMs(metrics.a2f_processing_ms)}</span>
          <span>1st Frame: {fmtMs(metrics.first_frame_ms)}</span>
          <span>Story: {fmtMs(metrics.storyteller_ms)}</span>
          <span>Total: {fmtMs(metrics.total_ms)}</span>
        </div>
      </div>

      <div className={styles.mainArea}>
        <div className={styles.chatCol}>
          <div className={styles.sessionInfo}>
            <div className={styles.sessionInfoMeta}>
              <div className={styles.statusCompact}>
                <div className={`${styles.statusDot} ${connected ? styles.connected : ''}`} />
                <span className={styles.statusLabel}>{connected ? 'Connected' : 'Disconnected'}</span>
              </div>
              {sessionId && (
                <span className={styles.sessionIdInfo} title="Session ID">{sessionId}</span>
              )}
              {(sessionCharacter || urlParams.character) && (
                <span className={styles.infoPill} title="Character">{sessionCharacter || urlParams.character}</span>
              )}
              {(sessionStory || urlParams.story) && (
                <span className={styles.infoPill} title="Story">{sessionStory || urlParams.story}</span>
              )}
              {activeFeatures.map(f => (
                <span key={f} className={`${styles.infoPill} ${styles.infoPillFeature}`} title="Feature">{f}</span>
              ))}
              <select
                className={styles.infoSelect}
                value={sessionMode || urlParams.mode || ''}
                onChange={e => setUrlParam('mode', e.target.value)}
                title="Connection mode"
              >
                <option value="">Blendshapes</option>
                <option value="bsstream">BS Stream</option>
                <option value="vertstream">Vert Stream</option>
                <option value="vertices">Vertices</option>
              </select>
              <select
                className={styles.infoSelect}
                value={sessionTransmission || urlParams.transmission || 'binary'}
                onChange={e => setUrlParam('transmission', e.target.value)}
                title="Transmission mode"
              >
                <option value="binary">Binary</option>
                <option value="json">JSON</option>
              </select>
              <select
                className={styles.infoSelect}
                value={mode}
                onChange={e => setMode(e.target.value)}
                title="Response mode"
              >
                <option value="TextOut">TextOut</option>
                <option value="TextIn">LLM (TextIn)</option>
              </select>
              {sessionId && (
                <button
                  className={`${styles.wsUrlBtn} ${wsCopied ? styles.wsUrlCopied : ''}`}
                  onClick={copyWsUrl}
                  title={buildWsUrl()}
                >
                  {wsCopied ? 'Copied!' : 'Copy WS URL'}
                </button>
              )}
            </div>
            <div className={styles.sessionInfoActions}>
              <button className={styles.btn} onClick={resetSession} title="Start a new Session">New Session</button>
              {!hasStoryteller && (
                <button className={styles.btn} onClick={startStorySession} title="Start a new Story Session">New Story Mode</button>
              )}
            </div>
          </div>

          <div className={styles.chat} ref={chatRef}>
            {messages.map(msg => (
              <div key={msg.id} className={`${styles.messageRow} ${styles[msg.type]}`}>
                <div className={`${styles.message} ${styles[msg.type]}`}>
                  {msg.text}
                </div>
                {msg.type === 'bot' && msg.animations && msg.animations.length > 0 && (
                  <div className={styles.animations}>
                    {msg.animations.map((a, i) => (
                      <span key={i} className={styles.animTag}>*{a.Name}*</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {suggestions.length > 0 && (
            <div className={styles.suggestionsArea}>
              <div className={styles.suggestionsLabel}>Suggested responses:</div>
              <div className={styles.suggestionsContainer}>
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    className={styles.chip}
                    onClick={() => { setInputText(s) }}
                    title={s}
                  >{s}</button>
                ))}
              </div>
            </div>
          )}

          <div className={styles.inputArea}>
            <input
              type="text"
              className={styles.input}
              placeholder="Type a message..."
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              autoComplete="off"
            />
            <button
              className={styles.sendBtn}
              onClick={handleSend}
              disabled={!connected}
            >Send</button>
          </div>
        </div>

        {a2fOpen && (
          <A2FSettingsSidebar onClose={() => setA2fOpen(false)} onApply={sendA2FConfig} />
        )}

        {hasStoryteller && storyDebugOpen && (
          <StoryDebugSidebar
            data={storyDebug}
            pending={storytellerPending}
            onClose={toggleStoryDebug}
          />
        )}
      </div>

      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}
    </div>
  )
}
